import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Center, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconAlertOctagonFilled } from '@tabler/icons-react';
import { FormProvider, useForm } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { publicSubmitForm, publicValidateEligibility } from '../api/publicEnrollment';
import { SubmissionError } from '../api/submit';
import FormAlert, { SYSTEM_ERROR_TITLE, systemErrorAlert, type FormAlertState } from '../components/FormAlert';
import FormField from '../components/FormField';
import PageShell from '../components/PageShell';
import { useResolvedProgram } from '../hooks/useResolvedProgram';
import type { FormPageConfig, SectionSchema } from '../types/schema';
import { buildPublicResultPath, resolveEnrollmentProduct } from '../utils/enrollmentProduct';

function PublicFormSection({
  section,
  isMobile,
}: {
  section: SectionSchema;
  isMobile: boolean;
}) {
  const columns = isMobile ? 1 : section.columns ?? 1;
  return (
    <Paper p="lg" withBorder radius="md" className="portal-panel">
      <Title order={4} mb={section.description ? 'xs' : 'md'} className="portal-section-title">
        {section.title}
      </Title>
      {section.description && <Text size="sm" c="dimmed" mb="md">{section.description}</Text>}
      {columns > 1 ? (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 'var(--mantine-spacing-sm)' }}>
          {section.fields.map((field) => (
            <div
              key={field.id}
              data-field-id={field.id}
              style={{ gridColumn: `span ${Math.min(field.span ?? 1, columns)}` }}
            >
              <FormField field={field} />
            </div>
          ))}
        </div>
      ) : (
        <Stack gap="sm">
          {section.fields.map((field) => (
            <div key={field.id} data-field-id={field.id}>
              <FormField field={field} />
            </div>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

function initialValues(pages: FormPageConfig[]): Record<string, unknown> {
  return Object.fromEntries(
    pages.flatMap((page) => page.sections.flatMap((section) => section.fields))
      .filter((field) => field.defaultValue !== undefined)
      .map((field) => [field.id, field.defaultValue]),
  );
}

export default function PublicFormPage() {
  const { data: config, isLoading, error } = useResolvedProgram();
  const { product } = useParams<{ product: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 48em)') ?? false;
  const [submitAlert, setSubmitAlert] = useState<FormAlertState>();
  const [requiredFieldsIncomplete, setRequiredFieldsIncomplete] = useState(false);
  const validationAlertRef = useRef<HTMLDivElement>(null);

  const productConfig = resolveEnrollmentProduct(config, product);
  const effectiveConfig = productConfig?.effectiveConfig ?? config;
  const siteConfig = productConfig
    ? { ...productConfig.effectiveConfig, siteType: 'enrollments' as const, pages: productConfig.pages }
    : null;
  const pages = siteConfig?.pages.form ?? [];
  const requestedPageIndex = (location.state as { enrollmentPage?: number } | null)?.enrollmentPage ?? 0;
  const pageIndex = Math.min(Math.max(requestedPageIndex, 0), Math.max(pages.length - 1, 0));
  const methods = useForm<Record<string, unknown>>({ defaultValues: initialValues(pages) });

  useEffect(() => {
    if (requiredFieldsIncomplete) {
      validationAlertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [requiredFieldsIncomplete]);

  if (isLoading) {
    return <Center h="100vh"><Loader size="lg" /></Center>;
  }
  if (error || !effectiveConfig || !siteConfig || pages.length === 0) {
    return <Navigate to="/" replace />;
  }

  const activePage = pages[Math.min(pageIndex, pages.length - 1)];
  const isLastPage = pageIndex >= pages.length - 1;
  const navigateToPage = (nextPageIndex: number) => {
    setRequiredFieldsIncomplete(false);
    navigate('.', { state: { enrollmentPage: nextPageIndex } });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Server errors for composite fields use dotted keys (e.g. "patient_address.city"); match on the root field ID.
  const findPageIndexForField = (errorKey: string): number => {
    const fieldId = errorKey.split('.')[0];
    return pages.findIndex((page) => page.sections.some((section) => section.fields.some((field) => field.id === fieldId)));
  };

  const showError = (message: string) => setSubmitAlert({ title: SYSTEM_ERROR_TITLE, message });
  // Highlights server field errors, moving to the page that holds the first one. Returns false
  // when the error carries no field errors.
  const applyFieldErrors = (err: unknown): boolean => {
    if (!(err instanceof SubmissionError) || !err.fieldErrors) {
      return false;
    }
    const entries = Object.entries(err.fieldErrors);
    if (entries.length === 0) {
      return false;
    }
    const targetPageIndex = findPageIndexForField(entries[0][0]);
    if (targetPageIndex !== -1 && targetPageIndex !== pageIndex) {
      navigateToPage(targetPageIndex);
    }
    entries.forEach(([field, message]) => {
      methods.setError(field, { type: 'server', message });
    });
    setSubmitAlert({
      title: 'Please correct the highlighted fields',
      message: 'Some of the information entered could not be accepted. Correct the highlighted fields below and submit again.',
    });
    return true;
  };
  // Server messages (e.g. "failed to submit enrollment") are not written for users, so anything
  // other than field errors gets the generic system message.
  const applyError = (err: unknown) => {
    if (!applyFieldErrors(err)) {
      setSubmitAlert(systemErrorAlert());
    }
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    methods.clearErrors();
    setSubmitAlert(undefined);
    setRequiredFieldsIncomplete(false);

    if (!product) {
      showError('This enrollment form requires a specific product and cannot be submitted here.');
      return;
    }

    try {
      if (activePage.action === 'validateEligibility') {
        // A failed check (non-2xx, WAF block, network error) is a system problem, not an answer
        // about the patient, so it always gets the generic message; the raw error is never shown.
        let eligibility;
        try {
          eligibility = await publicValidateEligibility(product, values);
        } catch (err) {
          applyError(err);
          return;
        }
        if (!eligibility.eligible) {
          setSubmitAlert({
            title: siteConfig.pages.ineligible?.title ?? 'Patient Not Eligible',
            message: siteConfig.pages.ineligible?.message ?? 'The patient is not eligible for this program.',
          });
          return;
        }
        if (pageIndex < pages.length - 1) {
          navigateToPage(pageIndex + 1);
          return;
        }
      }

      if (isLastPage) {
        const result = await publicSubmitForm(product, values);
        if (result.public_token) {
          navigate(buildPublicResultPath(product, result.public_token), { replace: true });
        } else {
          const reference = result.result_id || result.correlation_id;
          showError(`Your enrollment was submitted, but a result link could not be created. Reference ID: ${reference}`);
        }
      } else {
        navigateToPage(pageIndex + 1);
      }
    } catch (err) {
      applyError(err);
    }
  };

  const handleInvalidSubmit = (errors: FieldErrors<Record<string, unknown>>) => {
    setRequiredFieldsIncomplete(true);
    const firstFieldId = Object.keys(errors)[0];
    if (firstFieldId) {
      document.querySelector<HTMLElement>(`[data-field-id="${firstFieldId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <PageShell
      config={effectiveConfig}
      headerTitle={siteConfig.pages.title ?? effectiveConfig.theme.displayName}
      support={activePage.showSupportInfo === true ? effectiveConfig.support : undefined}
      containerSize="md"
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit, handleInvalidSubmit)} noValidate>
          <Stack gap="lg">
            <Text size="xs" c="dimmed"><Text component="span" c="red">*</Text> indicates required fields</Text>
            <div ref={validationAlertRef}>
              {requiredFieldsIncomplete && (
                <Alert
                  color="red"
                  icon={<IconAlertOctagonFilled />}
                  title={<Text c="#80170c" fw={700}>Please complete all required fields</Text>}
                  variant="light"
                >
                  <Text c="#80170c">
                    The form cannot be submitted until every field marked with * has been completed. Missing fields are highlighted below.
                  </Text>
                </Alert>
              )}
            </div>
            <FormAlert alert={submitAlert} />
            {activePage.sections.map((section) => (
              <PublicFormSection key={section.id} section={section} isMobile={isMobile} />
            ))}
            <Group justify="flex-end">
              {pageIndex === 0 && effectiveConfig.authenticationRequired === false && (
                <Button
                  type="button"
                  variant="default"
                  onClick={() => { window.location.href = `/${effectiveConfig.clientName}/${effectiveConfig.programName}`; }}
                >
                  Cancel
                </Button>
              )}
              {pageIndex > 0 && (
                <Button
                  type="button"
                  variant="default"
                  onClick={() => navigateToPage(pageIndex - 1)}
                >
                  Back
                </Button>
              )}
              <Button type="submit" className="portal-primary-button" loading={methods.formState.isSubmitting}>
                {activePage.submitLabel ?? (isLastPage ? 'Submit Enrollment' : 'Continue')}
              </Button>
            </Group>
          </Stack>
        </form>
      </FormProvider>
    </PageShell>
  );
}
